import { ChevronDown } from 'lucide-react';
import { WorkspaceMenu, type WorkspaceMenuItem } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';
import type { Criticality } from '@/lib/api/engagement-requirements';
import { CHOOSABLE_CRITICALITIES, CRITICALITY_LABEL } from './requirements-logic';

/**
 * Criticality chip and its row menu (T-0142).
 *
 * Tones follow `CRIT` in `docs/requirement-library/prototype/selection.js`
 * and the chip rules in the prototype tokens: "Not set" is a dashed
 * outline (`.chip-unset`), Critical is the amber chip, Desired the
 * neutral chip, Not required the unassessed chip and Not applicable the
 * frozen chip. Criticality is set per engagement (amendment 01, Decision
 * 31): a requirement loaded from the library starts "Not set".
 */

const TONE: Record<Criticality, string> = {
  not_set: 'border border-dashed border-ink-300 bg-transparent text-ink-500',
  critical: 'bg-accent-amber-100 text-accent-amber-700',
  desired: 'border border-ink-100 bg-paper-100 text-ink-700',
  not_required: 'bg-ink-100 text-posture-unassessed',
  not_applicable: 'bg-ink-100 text-ink-300',
};

export function CriticalityChip({ value, className }: { value: Criticality; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-meta font-medium',
        TONE[value],
        className,
      )}
    >
      {CRITICALITY_LABEL[value]}
    </span>
  );
}

/** Menu items offering the four choosable values. */
export function criticalityMenuItems(onChoose: (value: Criticality) => void): WorkspaceMenuItem[] {
  return CHOOSABLE_CRITICALITIES.map((c) => ({
    id: c,
    label: <CriticalityChip value={c} />,
    onSelect: () => onChoose(c),
  }));
}

/** The chip as a button that opens a menu to change the value. */
export function CriticalityMenu({
  value,
  number,
  onChange,
}: {
  value: Criticality;
  /** Requirement number, for the accessible name. */
  number: string;
  onChange: (value: Criticality) => void;
}) {
  return (
    <WorkspaceMenu
      aria-label={`Criticality of ${number}`}
      items={criticalityMenuItems(onChange)}
      trigger={
        <button
          type="button"
          aria-label={`Criticality of ${number}: ${CRITICALITY_LABEL[value]}. Change`}
          className="group/crit inline-flex items-center gap-0.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <CriticalityChip
            value={value}
            className="group-hover/crit:shadow-[0_0_0_1px_var(--ink-300)]"
          />
          <ChevronDown className="h-3 w-3 text-ink-300" aria-hidden="true" />
        </button>
      }
    />
  );
}
